import React, { useEffect, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import {
    BettingEventStatus,
    FreeplayLeague,
    HorseRaceEvent,
    HorseRaceWagerType,
    HorseRacingPrompt,
    OverviewEvent,
    PlatformEvent,
    PlatformOffer,
} from "@freeplay/shared-code";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { TrophyIcon } from "../components/icons/TrophyIcon";
import { GlobalStyles } from "../components/style/Style";
import { BetGroupOutcomeDisplay } from "../components/overview/BetGroupOutcomeDisplay";
import { MediaQueries } from "../components/hooks/dynamicStyleHook";
import { useServices } from "../contexts/ServiceContext";
import { GameTitle } from "../components/picks/GameTitle";
import { BetDisplay } from "../components/BetDisplay";
import { useEvent } from "../contexts/EventContext";
import DefaultImage from "../assets/default_background.png";
import DefaultTSImage from "../assets/default_background_ts.png";
import { groupGames, groupHorseRaceGames } from "./game/GameView";
import { GameType } from "../components/picks/GameGroup";

export interface PicksOverviewProps {
    testID?: string;
    isUser: boolean;
}

export const PickOverviewView = ({ isUser }: PicksOverviewProps): JSX.Element => {
    const { edgeDeploy } = useServices();
    const { currentEntry, currentEvent } = useEvent();
    const styles = useDynamicStyles(generateStyles);
    const [gameType, setGameType] = useState<GameType>(GameType.STANDARD);

    useEffect(() => {
        if (currentEvent?.horseRacingPrompts && currentEvent?.horseRacingPrompts.length !== 0) {
            setGameType(GameType.HORSE_RACING);
        } else {
            setGameType(GameType.STANDARD);
        }
    }, [currentEvent, setGameType]);

    if (!currentEvent) {
        return <></>;
    }

    const { betOutcomes, horseBetOutcome } = currentEntry ?? { betOutcomes: [] };
    const returnEvents: OverviewEvent[] = [];
    currentEvent.platformEvents.forEach((platformEvent: PlatformEvent, index: number) => {
        platformEvent.platformOffers.forEach((platformOffer: PlatformOffer) => {
            const returnEvent: Partial<OverviewEvent> & { platformEvents?: PlatformEvent[] } = {
                ...platformEvent,
            };
            delete returnEvent.platformEvents;
            returnEvent.offer = platformOffer;
            returnEvents.push(returnEvent as OverviewEvent);
        });
    });

    const gameEntry: [PlatformEvent, PlatformOffer][] = currentEvent.platformEvents.flatMap((entry) =>
        entry.platformOffers.map((item) => [entry, item]),
    ) as [PlatformEvent, PlatformOffer][];
    const horseGameEntry: [HorseRaceEvent, HorseRacingPrompt][] = currentEvent.horseRacingPrompts.map((prompt) => [
        prompt.horseRaceEvent,
        prompt,
    ]);
    const gameGroups: Map<number, [PlatformEvent, PlatformOffer][]> = groupGames(gameEntry);
    const horseGameGroup: Map<number, [HorseRaceEvent, HorseRacingPrompt][]> = groupHorseRaceGames(horseGameEntry);

    const getGroupedProps = () => {
        const elements: JSX.Element[] = [];
        if (gameType === GameType.STANDARD) {
            gameGroups.forEach((entry, index) => {
                elements.push(
                    <BetGroupOutcomeDisplay
                        key={`bet-outcome-group-${index}`}
                        gameEntries={entry}
                        groupName={entry[0][0].name}
                        groupDate={entry[0][0].startTime}
                        betOutcomes={betOutcomes}
                        isUser={isUser}
                    />,
                );
            });
        } else {
            let startIndex = 0;
            horseGameGroup.forEach((entry, index) => {
                elements.push(
                    <BetGroupOutcomeDisplay
                        key={`bet-outcome-group-${entry[0][0].id}`}
                        gameEntryWithHorseEvents={entry}
                        groupName={`${entry[0][0].trackDisplayName} - Race ${entry[0][0].raceId}`}
                        groupDate={parseISO(entry[0][0].correctedPostTime)}
                        horseBetOutcomes={horseBetOutcome}
                        isUser={isUser}
                        startIndex={startIndex}
                    />,
                );
                startIndex += entry.length;
            });
        }

        return elements;
    };

    const ContainerView = edgeDeploy ? View : ScrollView;
    let potentialHorseScore = 0;
    horseGameGroup.forEach((group) => {
        group.forEach((value) => {
            if (value[1].propType === HorseRaceWagerType.PICKEM) {
                potentialHorseScore += value[1].pickEm;
            } else {
                potentialHorseScore++;
            }
        });
    });

    const potentialScore = returnEvents.length > potentialHorseScore ? returnEvents.length : potentialHorseScore;
    return (
        <View style={[edgeDeploy ? styles.edgeContainer : styles.container]}>
            <GameTitle
                title={currentEvent.name}
                date={format(parseISO(currentEvent.bettingEnds), "MMMM dd, yyyy")}
                backgroundImage={currentEvent.game.league === FreeplayLeague.Other ? DefaultTSImage : DefaultImage}
            />

            <ContainerView style={styles.selectionContainer}>
                <View style={[styles.choiceContainer, edgeDeploy ? styles.edgeChoiceContainer : {}]}>
                    {getGroupedProps()}
                </View>
            </ContainerView>
            {currentEvent.eventStatus !== BettingEventStatus.RESULTED && (
                <View style={styles.infoContainer}>
                    <View style={styles.scoreContainer}>
                        <TrophyIcon fill={EStyleSheet.value(GlobalStyles.TROPHY)} />
                        <Text key={"Optional"} style={[styles.score, edgeDeploy ? styles.edgeScore : {}]}>
                            You can score up to {potentialScore} point{potentialScore === 1 ? "" : "s"}
                        </Text>
                    </View>
                    {currentEvent.game.league !== FreeplayLeague.HorseRacing && (
                        <BetDisplay
                            platformEvents={currentEvent.platformEvents}
                            entry={betOutcomes}
                            buttonText={currentEvent.buttonText}
                            buttonURL={currentEvent.buttonUrl}
                        />
                    )}
                </View>
            )}
        </View>
    );
};

const generateStyles = () =>
    EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeContainer: {
                padding: 15,
                borderRadius: 0,
                marginVertical: 0,
            },
            edgeMiddleButton: {
                marginHorizontal: "10px",
            },
            edgeChoiceContainer: {
                paddingHorizontal: 0,
            },
        },
        [MediaQueries.XSMALL]: {
            edgeScore: {
                fontSize: 18,
            },
        },
        edgeContainer: {
            width: "100%",
            maxWidth: 860,
            marginHorizontal: "auto",
            padding: 30,
            marginVertical: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            flex: 1,
            alignItems: "center",
        },
        container: {
            flex: 1,
            alignItems: "center",
        },
        infoContainer: {
            shadowOffset: {
                width: 0,
                height: 0,
            },
            shadowOpacity: 0.5,
            shadowRadius: 16,
            shadowColor: () => EStyleSheet.value(GlobalStyles.DEFAULT_SHADOW),
            width: "100%",
            minHeight: 70,
            paddingVertical: 10,
            paddingHorizontal: 10,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.GAME_CTA_BACKGROUND_COLOUR),
        },
        title: {
            fontSize: 32,
            lineHeight: 32,
            margin: 0,
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            color: () => EStyleSheet.value(GlobalStyles.ACCENT_COLOUR),
        },
        subTitle: {
            fontSize: 14,
            lineHeight: 14,
            fontWeight: "700",
            marginBottom: 5,
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
        },
        selectionContainer: {
            flex: 1,
            flexDirection: "column",
            alignSelf: "stretch",
        },
        bottomButtonsContainer: {
            width: "100%",
            height: 90,
            paddingTop: 20,
            paddingHorizontal: 20,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: () => EStyleSheet.value(GlobalStyles.BACKGROUND_COLOUR),
        },
        choiceContainer: {
            justifyContent: "center",
            paddingHorizontal: 20,
        },
        edgeChoiceContainer: {
            paddingHorizontal: 20,
        },
        scoreContainer: {
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            alignContent: "center",
        },
        score: {
            fontSize: 14,
            paddingLeft: 5,
            textAlign: "center",
            fontWeight: "700",
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
        },
        edgeScore: {
            fontSize: 24,
        },
    });
